import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { CATALOGUE_LANGUAGE, CATALOGUE_NAMESPACE, en } from './en';

/**
 * The i18next runtime for the web app (R18.9).
 *
 * A single instance, initialised synchronously from the in-bundle English
 * catalogue. Synchronous init matters for two reasons: the app has no async
 * translation backend at Foundation (English is compiled in, not fetched), and a
 * synchronous instance means `t()` returns real text on the very first render —
 * including server-side/static renders in tests — with no loading flash and no
 * key-as-text fallback.
 *
 * `keySeparator`/`nsSeparator` are left at i18next's defaults (`.`/`:`), so a
 * dotted validation `messageKey` such as `validation.title.length` resolves
 * straight through the nested catalogue.
 */
let initialised = false;

export function createI18n(): I18nInstance {
  // Initialise the default i18next instance rather than a private one. Binding it
  // as the react-i18next default means `useTranslation`/`t` resolve English even
  // where no `I18nextProvider` wraps the tree — the static component renders in
  // unit tests, most importantly — while the provider in the app root still binds
  // this same instance for the running app. Idempotent, so importing this module
  // more than once (test files, HMR) does not re-init.
  if (!initialised) {
    void i18next.use(initReactI18next).init({
      resources: {
        [CATALOGUE_LANGUAGE]: {
          [CATALOGUE_NAMESPACE]: en,
        },
      },
      lng: CATALOGUE_LANGUAGE,
      fallbackLng: CATALOGUE_LANGUAGE,
      defaultNS: CATALOGUE_NAMESPACE,
      // English only at Foundation; there is nothing to escape that React does not
      // already escape, and interpolated values are already-safe display strings.
      interpolation: { escapeValue: false },
      // A missing key is a catalogue bug, not a runtime condition to paper over, so
      // surface the key rather than an empty string.
      returnEmptyString: false,
    });
    initialised = true;
  }
  return i18next;
}

/**
 * The shared app instance. Importing this module initialises i18n as a side
 * effect, so any surface that renders through `useTranslation`/`t` — including a
 * static render in a unit test with no provider — resolves against the English
 * catalogue. The `I18nextProvider` in the app root binds this same instance into
 * the React tree explicitly.
 */
export const i18n = createI18n();
